import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import styled from "@emotion/styled";
import { WatchlistEntry } from "@rentablo/api/apiv1";
import { toCurrency, toPercent } from "@helpers";
import { DataBlockLayout } from "@rentablo/components";

interface Props {
  isFetching?: boolean;
  watchlistEntry?: WatchlistEntry;
}

const Block = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.subtle,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
}));

const Content = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignContent: "stretch",
  margin: theme.spacing(2),
  "& > :not(style)": { margin: 0, marginRight: theme.spacing(1), flexGrow: 1 },
  "& > *:last-child": { marginRight: 0 },
}));

export const GainLossStatistics = ({ isFetching, watchlistEntry }: Props) => {
  const { t } = useTranslation();

  return (
    <DataBlockLayout
      title={t("watchlistEntry.details.gainLossStatistics.title")}
      isFetching={isFetching}
    >
      <Content>
        <Row>
          <Block>
            <Typography color="textSecondary" variant="body3">
              {t("watchlistEntry.details.gainLossStatistics.gainAndLoss")}
            </Typography>
            <Typography
              variant="h3"
              data-testid="gainLossStatistics.gainAndLoss"
            >
              {!watchlistEntry && "N/A"}
              {watchlistEntry &&
                toPercent(watchlistEntry.returnOnInvestmentInPercent)}
            </Typography>
            <Typography variant="body3" color="textSecondary">
              {toCurrency(
                watchlistEntry?.returnOnInvestment,
                watchlistEntry?.currency
              )}
            </Typography>
          </Block>
        </Row>

        <Row data-testid="row-entryQuote">
          <Block>
            <Typography color="textSecondary" variant="body3">
              {t("watchlistEntry.details.gainLossStatistics.entryQuote")}
            </Typography>
            <Typography
              variant="h3"
              data-testid="gainLossStatistics.entryQuote"
            >
              {watchlistEntry
                ? toCurrency(
                    watchlistEntry?.purchasePrice,
                    watchlistEntry?.currency
                  )
                : "N/A"}
            </Typography>
          </Block>
        </Row>

        <Row data-testid="row-entryQuote">
          <Block>
            <Typography color="textSecondary" variant="body3">
              {t("watchlistEntry.details.gainLossStatistics.currentQuote")}
            </Typography>
            <Typography
              variant="h3"
              data-testid="gainLossStatistics.entryQuote"
            >
              {watchlistEntry
                ? toCurrency(
                    watchlistEntry.quote?.last,
                    watchlistEntry.currency
                  )
                : "N/A"}
            </Typography>
          </Block>
        </Row>

        {watchlistEntry && watchlistEntry?.oneYearAgoInPercent != null && (
          <Row data-testid="row-historic-returns">
            <Block>
              <Typography color="textSecondary" variant="body3">
                {t("watchlistEntry.details.gainLossStatistics.historicReturns")}
              </Typography>
              <Typography variant="body1">
                {t("ranges.years.oneYear")}:{" "}
                {toPercent(watchlistEntry.oneYearAgoInPercent)}
              </Typography>
              {watchlistEntry.threeYearsAgo != null && (
                <Typography variant="body1">
                  {t("ranges.years.threeYears")}:{" "}
                  {toPercent(watchlistEntry.threeYearsAgoInPercent)}
                </Typography>
              )}
              {watchlistEntry.fiveYearsAgo != null && (
                <Typography variant="body1">
                  {t("ranges.years.fiveYears")}:{" "}
                  {toPercent(watchlistEntry.fiveYearsAgoInPercent)}
                </Typography>
              )}
            </Block>
          </Row>
        )}
      </Content>
    </DataBlockLayout>
  );
};
